import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useCreateCryptoWallet } from '@cryptoHooks';

export const walletSchema = z.object({
  provider_name: z.string().trim().min(1, 'Provider name is required'),
  name: z.string().trim().min(1, 'Wallet name is required'),
  address: z.string().optional(),
  notes: z.string().optional(),
});

type WalletFormValues = z.infer<typeof walletSchema>;

interface WalletDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function WalletDialog({ open, onOpenChange, onSuccess }: WalletDialogProps) {
  const createWallet = useCreateCryptoWallet();

  const form = useForm<WalletFormValues>({
    resolver: zodResolver(walletSchema),
    defaultValues: {
      provider_name: '',
      name: '',
      address: '',
      notes: '',
    },
  });

  const { isSubmitting } = form.formState;
  const providerName = form.watch('provider_name');
  const walletName = form.watch('name');
  const isSubmitDisabled =
    isSubmitting || !providerName.trim() || !walletName.trim();

  const onSubmit = async (values: WalletFormValues) => {
    try {
      await createWallet.mutateAsync({
        provider_name: values.provider_name,
        name: values.name,
        address: values.address || null,
        notes: values.notes || null,
      });
      form.reset();
      onSuccess();
    } catch (error) {
      toast.error('Failed to create wallet. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Wallet</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="provider_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provider Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Coinbase, Binance, Ledger" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Wallet Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. My BTC Wallet" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Wallet address (optional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Additional notes (optional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitDisabled}>
                {isSubmitting ? 'Creating...' : 'Create Wallet'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
